import { FunctionComponent, useEffect, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import {
  Accordion as ReachAccordion,
  AccordionItem as ReachAccordionItem,
  AccordionButton as ReachAccordionButton,
  AccordionPanel as ReachAccordionPanel,
  useAccordionItemContext,
} from "@reach/accordion";

import { IconFrame } from "@/ui-kit";
import type { Glyph } from "@/ui-kit";

import { Typography } from "ui-kit/Typography";

import styles from "./Accordion.module.css";
import classNames from "classnames";
import Colors from "ui-kit/colors";
import { useElementHeight } from "hooks/useElementHeight";

export const AccordionPanel = ({
  children,
  hasIcon = false,
  className,
}: {
  children: ReactElement | string | null;
  hasIcon?: boolean;
  className?: string;
}) => {
  const { isExpanded } = useAccordionItemContext();
  const { ref, height } = useElementHeight();

  return (
    <ReachAccordionPanel
      className={classNames(
        styles.accordionPanel,
        {
          [styles.hasIcon]: hasIcon,
        },
        className
      )}
      hidden={false}
      aria-hidden={!isExpanded}
      style={{ maxHeight: isExpanded ? height : 0 }}
    >
      <div
        {...{ inert: !isExpanded ? "" : undefined }}
        ref={ref}
        className={classNames(
          styles.contentWrapper,
          typeof children === "string" && styles.contentWrapperString
        )}
      >
        {typeof children === "string" ? (
          <div className={styles.textContainer}>{children}</div>
        ) : (
          children
        )}
      </div>
    </ReachAccordionPanel>
  );
};

export const AccordionTitle = ({
  children,
  icon,
  className,
}: {
  children: string | ReactElement | ReactElement[] | null;
  icon?: Glyph | ReactElement;
  className?: string;
}) => {
  const { isExpanded } = useAccordionItemContext();

  return (
    <ReachAccordionButton
      className={classNames(styles.accordionTitle, className, {
        [styles.isExpanded]: isExpanded,
      })}
    >
      {typeof icon === "string" ? (
        <IconFrame
          icon={icon}
          className={styles.icon}
          iconColor={Colors.blue300}
        />
      ) : (
        icon
      )}
      {typeof children === "string" ? (
        <Typography className={styles.titleText} variant="body2">
          <strong>{children}</strong>
        </Typography>
      ) : (
        <>{children}</>
      )}

      <IconFrame
        icon={isExpanded ? "chevron-up" : "chevron-down"}
        iconSize="S"
        className={styles.iconChevron}
      />
    </ReachAccordionButton>
  );
};

export const AccordionItem = ({
  children,
  isHidden,
  className,
}: {
  children: ReactNode[] | ReactNode;
  isHidden?: boolean;
  className?: string;
}) => {
  return (
    <ReachAccordionItem
      className={classNames(styles.accordionItem, className, {
        [styles.isHidden]: isHidden,
      })}
    >
      {children}
    </ReachAccordionItem>
  );
};

export const Accordion = ({
  children,
  multiple,
  collapsible,
  index,
  className,
  defaultIndex,
}: {
  children: ReactNode | ReactNode[];
  multiple?: boolean;
  collapsible?: boolean;
  index?: number | number[];
  className?: string;
  defaultIndex?: number;
}) => {
  return (
    <ReachAccordion
      multiple={multiple}
      collapsible={collapsible}
      index={index}
      defaultIndex={defaultIndex}
      className={classNames(styles.accordion, className)}
    >
      {children}
    </ReachAccordion>
  );
};
